interface UnitLoadDimensions {
  unitLoadLength: number | null;
  unitLoadWidth: number | null;
}

export interface PalletPatternLookupInput {
  palletType: string;
  caseLength: number | string;
  caseWidth: number | string;
  tertiaryCaseLength?: number | string | null;
  tertiaryCaseWidth?: number | string | null;
}

export interface PalletPatternLookupRow {
  palletType: string;
  caseLength: number;
  caseWidth: number;
  casesPerLayer: number;
  palletPattern: string;
  count: number;
  lengthStart: number;
  lengthEnd: number;
  widthStart: number;
  widthEnd: number;
}

export interface PalletPatternLookupResult {
  palletPattern: string;
  casesPerLayer: number;
}

const normalizePalletType = (value: unknown): string => {
  const normalized = String(value ?? '').replace(/×/g, 'x').trim();
  const match = normalized.match(/\d+(?:\.\d+)?x\d+(?:\.\d+)?x\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)?/i);
  return (match?.[0] || normalized.split(/\s+/)[0] || '').toLowerCase();
};

const toFiniteNumber = (value: unknown): number | null => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizePackageMaterial = (value: string) => {
  const materialMap: Record<string, string> = {
    Aluminum: 'CAN',
    Glass: 'CAN',
    rPET: 'PET',
    Carton: 'PET',
    Multilayer: 'PET',
    Bag: 'PET',
  };

  return materialMap[value] || value;
};

export function findPalletPatternFromLookup(
  input: PalletPatternLookupInput,
  lookupRows: PalletPatternLookupRow[]
): PalletPatternLookupResult | null {
  const tertiaryLength = toFiniteNumber(input.tertiaryCaseLength);
  const tertiaryWidth = toFiniteNumber(input.tertiaryCaseWidth);
  const hasTertiaryDimensions =
    tertiaryLength !== null &&
    tertiaryWidth !== null &&
    tertiaryLength > 0 &&
    tertiaryWidth > 0;

  const effectiveCaseLength = hasTertiaryDimensions ? tertiaryLength : toFiniteNumber(input.caseLength);
  const effectiveCaseWidth = hasTertiaryDimensions ? tertiaryWidth : toFiniteNumber(input.caseWidth);

  if (effectiveCaseLength === null || effectiveCaseWidth === null) return null;

  const palletType = normalizePalletType(input.palletType);
  const match = lookupRows
    .filter((row) => normalizePalletType(row.palletType) === palletType)
    .filter((row) => (
      row.lengthStart <= effectiveCaseLength &&
      row.lengthEnd >= effectiveCaseLength &&
      row.widthStart <= effectiveCaseWidth &&
      row.widthEnd >= effectiveCaseWidth
    ))
    .sort((a, b) => b.count - a.count)[0];

  return match
    ? {
      palletPattern: match.palletPattern,
      casesPerLayer: match.casesPerLayer,
    }
    : null;
}

export function NewFormulaUnitloadLenWidth(
  packageMaterial: string,
  palletType: string,
  palletPattern: string,
  casesPerLayer: number,
  caseLength: number,
  caseWidth: number,
  specNumber: string,
  tertiaryCaseLength?: number | string | null,
  tertiaryCaseWidth?: number | string | null
): UnitLoadDimensions {
  // Initialize variables with default null values
  let unitLoadLength: number | null = null;
  let unitLoadWidth: number | null = null;

  const parsedTertiaryCaseLength = Number(tertiaryCaseLength);
  const parsedTertiaryCaseWidth = Number(tertiaryCaseWidth);
  const hasTertiaryDimensions =
    Number.isFinite(parsedTertiaryCaseLength) &&
    Number.isFinite(parsedTertiaryCaseWidth) &&
    parsedTertiaryCaseLength > 0 &&
    parsedTertiaryCaseWidth > 0;

  const effectiveCaseLength = hasTertiaryDimensions ? parsedTertiaryCaseLength : caseLength;
  const effectiveCaseWidth = hasTertiaryDimensions ? parsedTertiaryCaseWidth : caseWidth;
  packageMaterial = normalizePackageMaterial(packageMaterial);
  caseLength = effectiveCaseLength;
  caseWidth = effectiveCaseWidth;

  const specs = [
    'P0014704', 'P0014706', 'P0014708', 'P0014825', 'P0014826', 'P0014827', 'P0014843',
    'P0014853', 'P0014855', 'P0014858', 'P0015178', 'P0015208', 'P0015209', 'P0015214',
    'P0015216', 'P0015249', 'P0015256', 'P0015268', 'P0015271', 'P0015277', 'P0015297',
    'P0015307', 'P0015309', 'P0015314', 'P0015317', 'P0015318', 'P0015327', 'P0015329',
    'P0015966', 'P0022381', 'P0023284'
  ];

  const specs1 = ['P0015985', 'P0016029', 'P0023913'];

  if (packageMaterial === 'PET') {
    // Conditions for Pin Wheel pattern
    if (palletPattern === 'Pin Wheel') {
      if (casesPerLayer === 8) {
        unitLoadLength = caseLength + 2 * caseWidth;
        unitLoadWidth = caseLength + 2 * caseWidth;
      } else if (casesPerLayer === 10) {
        unitLoadLength = caseLength + 3 * caseWidth;
        unitLoadWidth = caseLength + 2 * caseWidth;
      } else if (casesPerLayer === 12) {
        unitLoadLength = caseLength * 2 + 2 * caseWidth;
        unitLoadWidth = caseLength * 2 + caseWidth;
      } else if (casesPerLayer === 22) {
        unitLoadLength = caseLength * 3 + 2 * caseWidth;
        unitLoadWidth = caseLength * 3 + caseWidth;
      } else if (casesPerLayer === 24) {
        unitLoadLength = caseLength * 2 + 3 * caseWidth;
        unitLoadWidth = caseLength * 2 + caseWidth * 3;
      } else if (casesPerLayer === 48) {
        unitLoadLength = caseLength * 2 + 7 * caseWidth;
        unitLoadWidth = caseLength * 2 + caseWidth * 5;
      }
    }
    // Conditions for Split Pin Wheel pattern
    else if (palletPattern === 'Split Pin Wheel') {
      if (casesPerLayer === 9) {
        unitLoadLength = caseLength * 2 + caseWidth;
        unitLoadWidth = caseLength + caseWidth * 2;
      } else if (casesPerLayer === 16) {
        unitLoadLength = caseLength * 3 + caseWidth;
        unitLoadWidth = caseLength + caseWidth * 3;
      } else if (casesPerLayer === 18) {
        unitLoadLength = caseLength + caseWidth * 4;
        unitLoadWidth = caseLength * 2 + caseWidth * 2;
      }
    }
    // Conditions for Row pattern
    else if (palletPattern === 'Row') {
      if (casesPerLayer === 4) {
        unitLoadLength = caseLength * 2;
        unitLoadWidth = caseWidth * 2;
      } else if (casesPerLayer === 6) {
        unitLoadLength = Math.max(caseLength * 2, caseWidth * 3);
        unitLoadWidth = Math.max(caseLength * 2, caseWidth * 3);
      } else if (casesPerLayer === 8) {
        unitLoadLength = caseLength * 2 + caseWidth;
        unitLoadWidth = Math.max(caseLength * 2, caseWidth * 3);
      } else if (casesPerLayer === 9) {
        unitLoadLength = caseLength * 3;
        unitLoadWidth = caseWidth * 3;
      } else if (casesPerLayer === 12) {
        unitLoadLength = Math.max(caseWidth * 4, caseLength * 3);
        unitLoadWidth = Math.min(caseWidth * 4, caseLength * 3);
      }
    }
    else if (palletPattern === 'Split Row') {
      if (casesPerLayer === 4) {
        unitLoadLength = Math.max(caseLength * 2, caseWidth * 2);
        unitLoadWidth = caseLength + caseWidth;
      } else if (casesPerLayer === 7) {
        unitLoadLength = caseLength + caseWidth * 2;
        unitLoadWidth = Math.max(caseLength * 2, caseWidth * 3);
      } else if (casesPerLayer === 8) {
        unitLoadLength = caseLength * 2 + caseWidth;
        unitLoadWidth = Math.max(caseLength * 2, caseWidth * 3);
      } else if (casesPerLayer === 9) {
        unitLoadLength = caseLength + 3 * caseWidth;
        unitLoadWidth = Math.max(caseLength * 2, caseWidth * 3);
      } else if (casesPerLayer === 10) {
        if (specs.includes(specNumber)) {
          unitLoadLength = caseLength * 2 + caseWidth;
          unitLoadWidth = Math.max(caseLength * 2, caseWidth * 4);
        } else if (specs1.includes(specNumber)) {
          unitLoadLength = Math.max(caseLength * 3, (caseLength + caseWidth * 3));
          unitLoadWidth = caseLength + caseWidth * 2;
        } else if (specNumber === 'P0014744') {
          unitLoadLength = caseLength + 3 * caseWidth;
          unitLoadWidth = Math.max(caseLength * 2, 4 * caseWidth);
        } else if (specNumber === 'P0014888') {
          unitLoadLength = Math.max((caseLength * 2 + caseWidth), (caseLength + 3 * caseWidth));
          unitLoadWidth = caseLength + 2 * caseWidth;
        } else if (specNumber === 'P0015961') {
          unitLoadLength = caseLength + 3 * caseWidth;
          unitLoadWidth = Math.max(caseLength * 2, (caseLength + 2 * caseWidth));
        } else {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 4);
          unitLoadWidth = caseLength + caseWidth * 2;
        }
      } else if (casesPerLayer === 11) {
        unitLoadLength = Math.max(caseLength * 3, caseWidth * 4);
        unitLoadWidth = caseLength * 2 + caseWidth;
      } else if (casesPerLayer === 12) {
        if (specNumber === 'P0016062') {
          unitLoadLength = Math.max(caseLength * 4, caseWidth * 4);
          unitLoadWidth = caseLength * 2 + caseWidth;
        } else {
          unitLoadLength = caseLength + caseWidth * 3;
          unitLoadWidth = Math.max((caseLength * 2 + caseWidth), (caseWidth * 4));
        }
      } else if (casesPerLayer === 13) {
        unitLoadLength = Math.max(caseLength * 3, caseWidth * 5);
        unitLoadWidth = caseLength * 2 + caseWidth;
      } else if (casesPerLayer === 15) {
        if (['P0016526', 'P0017391'].includes(specNumber)) {
          unitLoadLength = Math.max((caseLength * 2 + caseWidth * 2), caseWidth * 5);
          unitLoadWidth = Math.max(caseLength * 3, (caseWidth * 3 + caseLength));
        } else if (['P0022303', 'P0022311'].includes(specNumber)) {
          unitLoadLength = caseLength * 2 + caseWidth * 2;
          unitLoadWidth = Math.max(caseLength * 3, (caseLength + caseWidth * 3));
        } else if (['P0028390', 'P0029591', 'P0029592', 'P0016040'].includes(specNumber)) {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 6);
          unitLoadWidth = caseLength * 2 + caseWidth;
        } else {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 6);
          unitLoadWidth = caseLength + caseWidth * 3;
        }
      } else if (casesPerLayer === 17) {
        if (specNumber === 'P0031111') {
          unitLoadLength = Math.max(caseLength * 4, (caseLength + caseWidth * 4));
          unitLoadWidth = caseLength + caseWidth * 3;
        } else {
          unitLoadLength = Math.max(caseLength * 4, caseWidth * 5);
          unitLoadWidth = caseLength + caseWidth * 3;
        }
      } else if (casesPerLayer === 18) {
        unitLoadLength = caseLength * 3 + caseWidth;
        unitLoadWidth = Math.max(caseLength * 3, caseWidth * 5);
      } else if (casesPerLayer === 22) {
        unitLoadLength = Math.max(caseLength * 4, caseWidth * 6);
        unitLoadWidth = caseLength * 3 + caseWidth;
      } else if (casesPerLayer === 24) {
        if (['P0022336', 'P0022363'].includes(specNumber)) {
          unitLoadLength = caseLength * 3 + caseWidth * 2;
          unitLoadWidth = Math.max(caseLength * 4, (caseLength + caseWidth * 4));
        } else {
          unitLoadLength = caseLength * 4 + caseWidth;
          unitLoadWidth = Math.max(caseLength * 4, caseWidth * 5);
        }
      } else if (casesPerLayer === 26) {
        unitLoadLength = Math.max(caseLength * 5, caseWidth * 6);
        unitLoadWidth = caseLength + caseWidth * 4;
      } else if (casesPerLayer === 28) {
        unitLoadLength = caseLength * 4 + caseWidth;
        unitLoadWidth = Math.max(caseLength * 4, caseWidth * 6);
      } else if (casesPerLayer === 36) {
        if (specNumber === 'P0018118') {
          unitLoadLength = caseLength * 3 + caseWidth * 3;
          unitLoadWidth = Math.max(caseLength * 4, caseWidth * 8);
        } else {
          unitLoadLength = caseLength * 4 + caseWidth;
          unitLoadWidth = Math.max(caseLength * 4, caseWidth * 8);
        }
      } else if (casesPerLayer === 40) {
        if (['P0017174', 'P0018152'].includes(specNumber)) {
          unitLoadLength = caseLength * 4 + caseWidth * 2;
          unitLoadWidth = caseWidth * 8;
        } else {
          unitLoadLength = Math.max(caseLength * 5, caseWidth * 10);
          unitLoadWidth = caseLength + caseWidth * 6;
        }
      }
    }
  } else if (packageMaterial === 'CAN') {
    if (palletType === '48x40x5' || palletType === '48x40x5.18' || palletType === '48x40x5.5/5.6') {
      if (palletPattern === 'Pin Wheel') {
        if (casesPerLayer === 8) {
          unitLoadLength = caseLength + 2 * caseWidth;
          unitLoadWidth = caseLength + 2 * caseWidth;
        } else if (casesPerLayer === 10) {
          unitLoadLength = caseLength + 3 * caseWidth;
          unitLoadWidth = caseLength + 2 * caseWidth;
        } else if (casesPerLayer === 13) {
          unitLoadLength = caseLength * 2 + 2 * caseWidth;
          unitLoadWidth = caseLength * 2 + caseWidth;
        } else if (casesPerLayer === 20) {
          unitLoadLength = caseLength * 3 + 2 * caseWidth;
          unitLoadWidth = caseLength * 2 + caseWidth * 2;
        }
      }

      if (palletPattern === 'Split Pin Wheel') {
        if (casesPerLayer === 9) {
          unitLoadLength = caseLength * 2 + caseWidth;
          unitLoadWidth = caseLength + caseWidth * 2;
        } else if (casesPerLayer === 19) {
          unitLoadLength = caseLength * 2 + 3 * caseWidth;
          unitLoadWidth = caseLength * 2 + caseWidth * 2;
        } else if (casesPerLayer === 16) {
          unitLoadLength = caseLength * 3 + caseWidth;
          unitLoadWidth = caseLength + caseWidth * 3;
        } else if (casesPerLayer === 13) {
          unitLoadLength = caseLength * 2 + 2 * caseWidth;
          unitLoadWidth = caseLength * 2 + caseWidth;
        }
      }

      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 10) {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 4);
          unitLoadWidth = caseLength + caseWidth * 2;
        } else if (casesPerLayer === 11) {
          unitLoadLength = Math.max(caseLength * 4, caseWidth * 3);
          unitLoadWidth = caseLength + caseWidth * 2;
        } else if (casesPerLayer === 15) {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 6);
          unitLoadWidth = caseLength * 2 + caseWidth;
        } else if (casesPerLayer === 16) {
          if (specNumber === 'P0034231') {
            unitLoadLength = caseLength + caseWidth * 4;
            unitLoadWidth = Math.max(caseLength * 3, caseWidth * 4);
          } else {
            unitLoadLength = Math.max((caseLength * 3 + caseWidth), (caseWidth * 5));
            unitLoadWidth = caseLength + caseWidth * 3;
          }
        } else if (casesPerLayer === 20) {
          if (['P0015759', 'P0034677'].includes(specNumber)) {
            unitLoadLength = Math.max(caseLength * 3, (caseLength + caseWidth * 6));
            unitLoadWidth = caseLength * 2 + caseWidth;
          } else {
            unitLoadLength = Math.max(caseLength * 6, caseWidth * 4);
            unitLoadWidth = caseLength * 2 + caseWidth * 2;
          }
        } else if (casesPerLayer === 21) {
          unitLoadLength = caseLength * 3 + caseWidth;
          unitLoadWidth = Math.max(caseLength * 3, caseWidth * 6);
        } else if (casesPerLayer === 24) {
          if (specNumber === 'P0015772') {
            unitLoadLength = caseLength * 4 + caseWidth;
            unitLoadWidth = Math.max(caseLength * 4, caseWidth * 5);
          } else {
            unitLoadLength = Math.max(caseLength * 3, caseWidth * 9);
            unitLoadWidth = caseLength * 2 + caseWidth * 2;
          }
        } else if (casesPerLayer === 25) {
          unitLoadLength = Math.max(caseLength * 4, caseWidth * 7);
          unitLoadWidth = caseLength * 3 + caseWidth;
        }
      }
    }

    if (palletType === '48x36x5') {
      if (palletPattern === 'Pin Wheel') {
        if (casesPerLayer === 10) {
          unitLoadLength = caseLength + 3 * caseWidth;
          unitLoadWidth = caseLength + 2 * caseWidth;
        } else if (casesPerLayer === 20) {
          unitLoadLength = caseLength + 6 * caseWidth;
          unitLoadWidth = caseLength + caseWidth * 4;
        }
      }

      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 10) {
          if (specNumber === 'P0015426') {
            unitLoadLength = Math.max(caseLength * 3, (caseLength + caseWidth * 3));
            unitLoadWidth = caseLength + caseWidth * 2;
          } else {
            unitLoadLength = Math.max(caseLength * 3, caseWidth * 4);
            unitLoadWidth = caseLength + caseWidth * 2;
          }
        } else if (casesPerLayer === 20) {
          unitLoadLength = Math.max(caseLength * 4, caseWidth * 6);
          unitLoadWidth = caseLength * 2 + caseWidth * 2;
        }
      }
    }

    if (palletType === '36x36x5') {
      if (palletPattern === 'Split Pin Wheel') {
        if (casesPerLayer === 9) {
          unitLoadLength = caseLength * 2 + caseWidth;
          unitLoadWidth = caseLength + caseWidth * 2;
        }
      }

      if (palletPattern === 'Pin Wheel') {
        if (casesPerLayer === 16) {
          if (specNumber === 'P0015764') {
            unitLoadLength = caseLength + caseWidth * 4;
            unitLoadWidth = caseLength + caseWidth * 4;
          } else {
            unitLoadLength = caseLength * 2 + caseWidth * 2;
            unitLoadWidth = caseLength * 2 + caseWidth * 2;
          }
        }
      }
    }

    if (palletType === '42x36x5') {
      if (palletPattern === 'Split Pin Wheel') {
        if (casesPerLayer === 18) {
          unitLoadLength = caseLength * 2 + caseWidth * 2;
          unitLoadWidth = caseLength + caseWidth * 4;
        }
      }

      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 9) {
          unitLoadLength = Math.max(caseLength * 2 + caseWidth, caseWidth * 4);
          unitLoadWidth = caseLength + caseWidth * 2;
        }
      }
    }

    if (palletType === '47x36x5') {
      if (palletPattern === 'Split Pin Wheel') {
        if (casesPerLayer === 9) {
          unitLoadLength = caseLength * 2 + caseWidth;
          unitLoadWidth = caseLength + caseWidth * 2;
        }
      }
    }

    if (palletType === '48x37x5') {
      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 10) {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 4);
          unitLoadWidth = caseLength + caseWidth * 2;
        }
      }
    }

    if (palletType === '48x41x5') {
      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 24) {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 9);
          unitLoadWidth = caseLength * 2 + caseWidth * 2;
        }
      }
    }

    if (palletType === '48x42x5') {
      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 10) {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 4);
          unitLoadWidth = caseLength + caseWidth * 2;
        }
      }
    }

    if (palletType === '48x43x5') {
      if (palletPattern === 'Pin Wheel') {
        if (casesPerLayer === 10) {
          unitLoadLength = caseLength + 3 * caseWidth;
          unitLoadWidth = caseLength + 2 * caseWidth;
        }
      }
    }

    if (palletType === '48x44x5') {
      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 24) {
          unitLoadLength = Math.max(caseLength * 4, caseWidth * 6);
          unitLoadWidth = caseLength * 2 + caseWidth * 3;
        }
      }
    }

    if (palletType === '48x44x6') {
      if (palletPattern === 'Split Row') {
        if (casesPerLayer === 10) {
          unitLoadLength = Math.max(caseLength * 3, caseWidth * 4);
          unitLoadWidth = caseLength + caseWidth * 2;
        }
      }
    }
  }

  if (unitLoadLength === null || unitLoadWidth === null) {
    try {
      const palletMatch = String(palletType ?? '').replace(/×/g, 'x').match(/(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/i);
      const palletLength = palletMatch ? Number(palletMatch[1]) : null;
      const palletWidth = palletMatch ? Number(palletMatch[2]) : null;

      if (
        palletLength !== null &&
        palletWidth !== null &&
        Number.isFinite(palletLength) &&
        Number.isFinite(palletWidth) &&
        caseLength > 0 &&
        caseWidth > 0 &&
        casesPerLayer > 0
      ) {
        const cols = Math.max(1, Math.round(palletLength / caseLength));
        const rows = Math.ceil(casesPerLayer / cols);
        const dim1 = Math.min(cols * caseLength, palletLength + 3);
        const dim2 = Math.min(rows * caseWidth, palletWidth + 3);
        unitLoadLength = Number(Math.max(dim1, dim2).toFixed(1));
        unitLoadWidth = Number(Math.min(dim1, dim2).toFixed(1));
      }
    } catch {
      // Leave values null when dimensions cannot be inferred.
    }
  }

  return {
    unitLoadLength: unitLoadLength === null ? null : Number(unitLoadLength.toFixed(2)),
    unitLoadWidth: unitLoadWidth === null ? null : Number(unitLoadWidth.toFixed(2))
  };
}
